import type { StateCreator } from 'zustand';
import { logger } from '@/utils/logger';
import { trackMatchStarted } from '@/lib/analytics/game-events';
import type {
  MatchAnswerAckPayload,
  MatchCluesGuessAckPayload,
  MatchCountdownGuessAckPayload,
  MatchCountdownPayload,
  MatchFinalResultsPayload,
  MatchOpponentCountdownProgressPayload,
  MatchPartyStatePayload,
  MatchRoundResultPayload,
  MatchStartPayload,
  MatchStatePayload,
  MatchWaitingForReadyPayload,
  ResolvedMatchQuestionPayload,
} from '@/lib/realtime/socket.types';
import type { CancelledMatchStatus, DevPossessionAnimation, MatchStatus, RealtimeState } from './types';
import {
  constructFallbackMatchFromResults,
  extractPlayerTotals,
  hasTimingRefresh as hasQuestionTimingRefresh,
  isStaleAnswerAck,
  isStaleStateEvent,
  mergeOpponentFromFinalParticipants,
  parseCountdownDeadline,
  resolvePartyPlayers,
  shouldBufferQuestion,
  shouldClearCountdownOnNewQuestion,
  shouldClearCountdownOnStateChange,
  shouldClearQuestionOnStateChange,
} from './reducers';

/** Client-side fallback countdown until the server's match:countdown event arrives with the real value. */
const DEFAULT_COUNTDOWN_MS = 5000;
const PARTY_QUIZ_DEFAULT_COUNTDOWN_MS = 5000;

/**
 * Active match state. Owns the `match` field (which holds all gameplay state:
 * current question, answer acks, scores, possession/party state, etc.) plus
 * the dev-only animation trigger.
 *
 * Cross-slice writes (still safe under Zustand 5 typed StateCreator):
 * - setMatchStart wipes lobby + draft + presence state alongside seeding match.
 * - exitCompletedMatchToLobby wipes match + draft + presence + dev anim + error.
 */
export interface MatchSlice {
  match: MatchStatus | null;
  cancelledMatch: CancelledMatchStatus | null;
  devPossessionAnimation: DevPossessionAnimation | null;
  setMatchStart: (payload: MatchStartPayload) => void;
  setMatchCountdown: (payload: MatchCountdownPayload) => void;
  setMatchWaitingForReady: (payload: MatchWaitingForReadyPayload) => void;
  setMatchState: (payload: MatchStatePayload) => void;
  setPartyState: (payload: MatchPartyStatePayload) => void;
  setMatchQuestion: (payload: ResolvedMatchQuestionPayload) => void;
  promotePendingQuestion: () => void;
  setAnswerAck: (payload: MatchAnswerAckPayload) => void;
  setCountdownGuessAck: (payload: MatchCountdownGuessAckPayload) => void;
  setOpponentCountdownProgress: (payload: MatchOpponentCountdownProgressPayload) => void;
  setCluesGuessAck: (payload: MatchCluesGuessAckPayload) => void;
  setOpponentAnswered: (payload?: {
    matchId?: string;
    qIndex?: number;
    opponentTotalPoints?: number;
    pointsEarned?: number;
    isCorrect?: boolean;
    selectedIndex?: number | null;
  }) => void;
  setQuestionPhase: (phase: 'reveal' | 'playing') => void;
  setRoundResult: (payload: MatchRoundResultPayload) => void;
  setFinalResults: (payload: MatchFinalResultsPayload) => void;
  setMatchCancelled: (payload: CancelledMatchStatus) => void;
  triggerDevPossessionAnimation: (payload: { result: 'goal' | 'saved' | 'miss'; attackerSeat: 1 | 2 }) => void;
  clearDevPossessionAnimation: () => void;
  exitCompletedMatchToLobby: () => void;
}

export const matchInitialState = {
  match: null,
  cancelledMatch: null,
  devPossessionAnimation: null,
} as const satisfies Pick<MatchSlice, 'match' | 'cancelledMatch' | 'devPossessionAnimation'>;

export const createMatchSlice: StateCreator<RealtimeState, [], [], MatchSlice> = (set) => ({
  ...matchInitialState,

  setMatchStart: (payload) => {
    logger.info('Realtime store set match start', { matchId: payload.matchId, opponentId: payload.opponent.id });
    try {
      const isAiOpponent = !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(payload.opponent.id ?? '');
      trackMatchStarted({
        matchId: payload.matchId,
        mode: payload.mode,
        variant: payload.variant,
        opponentIsAi: isAiOpponent,
        opponentRp: payload.opponent.rp,
      });
    } catch {
      /* analytics best-effort */
    }
    set({
      lobby: null,
      draft: null,
      matchPaused: false,
      pauseUntil: null,
      pausedAt: null,
      remainingReconnects: null,
      draftPaused: false,
      draftPauseUntil: null,
      draftDisconnectedUserId: null,
      rejoinMatch: null,
      forfeitPending: null,
      partyDropout: null,
      match: {
        matchId: payload.matchId,
        mode: payload.mode,
        variant: payload.variant,
        mySeat: payload.mySeat ?? null,
        opponent: payload.opponent,
        myRecentForm: payload.myRecentForm,
        participants: payload.participants,
        categoryName: payload.categoryName,
        countdownEndsAt: Date.now() + (payload.variant === 'friendly_party_quiz'
          ? PARTY_QUIZ_DEFAULT_COUNTDOWN_MS
          : DEFAULT_COUNTDOWN_MS),
        countdownReason: 'kickoff',
        waitingForReady: null,
        currentQuestion: null,
        pendingQuestion: null,
        questions: {},
        answerAck: null,
        countdownGuessAck: null,
        opponentCountdownFoundCount: 0,
        cluesGuessAck: null,
        opponentAnswered: false,
        opponentSelectedIndex: null,
        myTotalPoints: 0,
        oppTotalPoints: 0,
        opponentRecentPoints: 0,
        lastRoundResult: null,
        finalResults: null,
        currentQuestionPhase: 'reveal',
        opponentAnsweredCorrectly: null,
        possessionState: null,
        partyState: null,
        stateVersion: 0,
        serverTimeOffsetMs: null,
      },
    });
  },

  setMatchCountdown: (payload) => {
    logger.info('Realtime store set match countdown', {
      matchId: payload.matchId,
      seconds: payload.seconds,
      startsAt: payload.startsAt,
      reason: payload.reason,
    });
    set((state) => {
      if (!state.match || state.match.matchId !== payload.matchId) return state;
      const startsAtMs = parseCountdownDeadline(payload.startsAt, payload.seconds, Date.now());
      const isResume = payload.reason === 'resume';
      return {
        matchPaused: isResume ? true : state.matchPaused,
        pauseUntil: isResume ? null : state.pauseUntil,
        pausedAt: state.pausedAt,
        remainingReconnects: isResume ? null : state.remainingReconnects,
        match: {
          ...state.match,
          countdownEndsAt: startsAtMs,
          countdownReason: payload.reason ?? 'kickoff',
          waitingForReady: null,
          serverTimeOffsetMs: payload.serverTimeOffsetMs ?? state.match.serverTimeOffsetMs,
        },
      };
    });
  },

  setMatchWaitingForReady: (payload) => {
    logger.info('Realtime store set match waiting for ready', {
      matchId: payload.matchId,
      phase: payload.phase,
      readyCount: payload.readyCount,
      totalCount: payload.totalCount,
      forceStartsAt: payload.forceStartsAt,
    });
    set((state) => {
      if (!state.match || state.match.matchId !== payload.matchId) return state;
      const forceStartsAtMs = new Date(payload.forceStartsAt).getTime();
      return {
        matchPaused: payload.phase === 'resume' ? true : state.matchPaused,
        pauseUntil: payload.phase === 'resume' ? null : state.pauseUntil,
        pausedAt: state.pausedAt,
        remainingReconnects: payload.phase === 'resume' ? null : state.remainingReconnects,
        match: {
          ...state.match,
          waitingForReady: {
            ...payload,
            forceStartsAtMs: Number.isFinite(forceStartsAtMs) ? forceStartsAtMs : Date.now(),
          },
          serverTimeOffsetMs: payload.serverTimeOffsetMs ?? state.match.serverTimeOffsetMs,
        },
      };
    });
  },

  setMatchState: (payload) => {
    logger.info('Realtime store set match state', {
      matchId: payload.matchId,
      phase: payload.phase,
      half: payload.half,
      possessionDiff: payload.possessionDiff,
      phaseKind: payload.phaseKind,
      phaseRound: payload.phaseRound,
      stateVersion: payload.stateVersion,
    });
    set((state) => {
      if (!state.match || state.match.matchId !== payload.matchId) return state;
      const incomingVersion = payload.stateVersion ?? 0;
      if (isStaleStateEvent(incomingVersion, state.match.stateVersion)) {
        logger.warn('Ignoring stale match:state event', {
          incoming: incomingVersion,
          current: state.match.stateVersion,
        });
        return state;
      }

      const shouldClearQuestion = shouldClearQuestionOnStateChange(
        state.match.possessionState?.phase,
        payload,
        Boolean(state.match.lastRoundResult),
      );
      const shouldClearCountdown = shouldClearCountdownOnStateChange(payload);
      return {
        match: {
          ...state.match,
          possessionState: payload,
          stateVersion: incomingVersion > 0 ? incomingVersion : state.match.stateVersion,
          countdownEndsAt: shouldClearCountdown ? null : state.match.countdownEndsAt,
          countdownReason: shouldClearCountdown ? null : state.match.countdownReason,
          waitingForReady: shouldClearCountdown ? null : state.match.waitingForReady,
          currentQuestion: shouldClearQuestion ? null : state.match.currentQuestion,
          pendingQuestion: shouldClearQuestion ? null : state.match.pendingQuestion,
          answerAck: shouldClearQuestion ? null : state.match.answerAck,
          countdownGuessAck: shouldClearQuestion ? null : state.match.countdownGuessAck,
          cluesGuessAck: shouldClearQuestion ? null : state.match.cluesGuessAck,
          lastRoundResult: shouldClearQuestion ? null : state.match.lastRoundResult,
          opponentAnswered: shouldClearQuestion ? false : state.match.opponentAnswered,
          opponentSelectedIndex: shouldClearQuestion ? null : state.match.opponentSelectedIndex,
          opponentRecentPoints: shouldClearQuestion ? 0 : state.match.opponentRecentPoints,
          opponentAnsweredCorrectly: shouldClearQuestion ? null : state.match.opponentAnsweredCorrectly,
          currentQuestionPhase: shouldClearQuestion ? 'reveal' : state.match.currentQuestionPhase,
        },
      };
    });
  },

  setPartyState: (payload) => {
    logger.info('Realtime store set party state', {
      matchId: payload.matchId,
      currentQuestionIndex: payload.currentQuestionIndex,
      leaderUserId: payload.leaderUserId,
      stateVersion: payload.stateVersion,
    });
    set((state) => {
      if (!state.match || state.match.matchId !== payload.matchId) return state;
      const incomingVersion = payload.stateVersion ?? 0;
      const currentVersion = state.match.stateVersion;
      // party_state additionally requires currentVersion > 0 — version 0 means
      // we've never received a state event yet and any party_state may arrive.
      if (currentVersion > 0 && isStaleStateEvent(incomingVersion, currentVersion)) {
        logger.warn('Ignoring stale match:party_state event', {
          incoming: incomingVersion,
          current: currentVersion,
        });
        return state;
      }

      const { myPlayer, firstOpponent } = resolvePartyPlayers(payload.players, state.selfUserId);

      return {
        match: {
          ...state.match,
          partyState: payload,
          stateVersion: incomingVersion > 0 ? incomingVersion : state.match.stateVersion,
          myTotalPoints: myPlayer?.totalPoints ?? state.match.myTotalPoints,
          oppTotalPoints: firstOpponent?.totalPoints ?? state.match.oppTotalPoints,
        },
      };
    });
  },

  setMatchQuestion: (payload) => {
    logger.info('Realtime store set match question', {
      matchId: payload.matchId,
      qIndex: payload.qIndex,
      questionKind: payload.question.kind,
      questionPrompt: payload.question.prompt,
      questionPromptPreview: payload.question.prompt?.substring(0, 50) + '...',
    });
    set((state) => {
      if (!state.match) return state;
      if (state.match.matchId !== payload.matchId) {
        logger.warn('Ignoring mismatched match:question event', {
          activeMatchId: state.match.matchId,
          payloadMatchId: payload.matchId,
        });
        return state;
      }
      // Guard: ignore stale/out-of-order question events
      const currentQIndex = state.match.currentQuestion?.qIndex ?? -1;
      if (payload.qIndex < currentQIndex) {
        logger.warn('Ignoring stale match:question event', {
          received: payload.qIndex,
          current: currentQIndex,
        });
        return state;
      }

      if (payload.qIndex === currentQIndex) {
        const hasTimingRefresh = hasQuestionTimingRefresh(state.match.currentQuestion, payload);
        if ((!state.matchPaused && !hasTimingRefresh) || !state.match.currentQuestion) {
          logger.warn('Ignoring duplicate match:question event for active question', {
            qIndex: payload.qIndex,
            matchPaused: state.matchPaused,
            hasTimingRefresh,
          });
          return state;
        }

        logger.info('Refreshing current question timing after pause/rejoin', {
          qIndex: payload.qIndex,
          questionKind: payload.question.kind,
        });

        return {
          match: {
            ...state.match,
            currentQuestion: payload,
            pendingQuestion: null,
            waitingForReady: null,
            countdownEndsAt: state.match.countdownReason === 'kickoff' ? null : state.match.countdownEndsAt,
            countdownReason: state.match.countdownReason === 'kickoff' ? null : state.match.countdownReason,
            serverTimeOffsetMs: payload.serverTimeOffsetMs ?? state.match.serverTimeOffsetMs,
            questions: {
              ...state.match.questions,
              [payload.qIndex]: {
                payload,
                correctIndex: payload.correctIndex ?? state.match.questions[payload.qIndex]?.correctIndex,
              },
            },
          },
        };
      }

      // Buffer question if we're still showing the last round result.
      // Exception: when server state is HALFTIME, accept question immediately to avoid
      // getting stuck if phase transition and question packets arrive out of order.
      if (shouldBufferQuestion(Boolean(state.match.lastRoundResult), state.match.possessionState?.phase)) {
        logger.info('Buffering match:question as pendingQuestion (result still showing)', {
          qIndex: payload.qIndex,
        });
        const shouldClearCountdown = shouldClearCountdownOnNewQuestion(
          payload.qIndex,
          state.match.countdownReason,
        );
        return {
          match: {
            ...state.match,
            pendingQuestion: payload,
            waitingForReady: null,
            countdownEndsAt: shouldClearCountdown ? null : state.match.countdownEndsAt,
            countdownReason: shouldClearCountdown ? null : state.match.countdownReason,
            serverTimeOffsetMs: payload.serverTimeOffsetMs ?? state.match.serverTimeOffsetMs,
            questions: {
              ...state.match.questions,
              [payload.qIndex]: {
                payload,
                correctIndex: payload.correctIndex ?? state.match.questions[payload.qIndex]?.correctIndex,
              },
            },
          },
        };
      }

      logger.info('Store updated with new question', {
        qIndex: payload.qIndex,
        prompt: payload.question.prompt,
        options: payload.question.kind === 'multipleChoice' ? payload.question.options : [],
        categoryName: payload.question.categoryName,
      });

      const shouldClearCountdown = shouldClearCountdownOnNewQuestion(
        payload.qIndex,
        state.match.countdownReason,
      );
      return {
        match: {
          ...state.match,
          currentQuestion: payload,
          pendingQuestion: null,
          waitingForReady: null,
          countdownEndsAt: shouldClearCountdown ? null : state.match.countdownEndsAt,
          countdownReason: shouldClearCountdown ? null : state.match.countdownReason,
          serverTimeOffsetMs: payload.serverTimeOffsetMs ?? state.match.serverTimeOffsetMs,
          answerAck: null,
          countdownGuessAck: null,
          opponentCountdownFoundCount: 0,
          cluesGuessAck: null,
          opponentAnswered: false,
          opponentSelectedIndex: null,
          opponentRecentPoints: 0,
          lastRoundResult: null,
          currentQuestionPhase: 'reveal',
          opponentAnsweredCorrectly: null,
          questions: {
            ...state.match.questions,
            [payload.qIndex]: {
              payload,
              correctIndex: payload.correctIndex ?? state.match.questions[payload.qIndex]?.correctIndex,
            },
          },
        },
      };
    });
  },

  promotePendingQuestion: () => {
    set((state) => {
      if (!state.match || !state.match.pendingQuestion) return state;
      const pending = state.match.pendingQuestion;
      logger.info('Promoting pending question to current', { qIndex: pending.qIndex });
      const shouldClearCountdown = shouldClearCountdownOnNewQuestion(
        pending.qIndex,
        state.match.countdownReason,
      );
      return {
        match: {
          ...state.match,
          currentQuestion: pending,
          pendingQuestion: null,
          waitingForReady: null,
          countdownEndsAt: shouldClearCountdown ? null : state.match.countdownEndsAt,
          countdownReason: shouldClearCountdown ? null : state.match.countdownReason,
          answerAck: null,
          countdownGuessAck: null,
          opponentCountdownFoundCount: 0,
          cluesGuessAck: null,
          opponentAnswered: false,
          opponentSelectedIndex: null,
          opponentRecentPoints: 0,
          lastRoundResult: null,
          currentQuestionPhase: 'reveal',
          opponentAnsweredCorrectly: null,
          serverTimeOffsetMs: pending.serverTimeOffsetMs ?? state.match.serverTimeOffsetMs,
        },
      };
    });
  },

  setAnswerAck: (payload) => {
    logger.info('Realtime store set answer ack', {
      matchId: payload.matchId,
      qIndex: payload.qIndex,
      isCorrect: payload.isCorrect,
    });
    set((state) => {
      if (!state.match) return state;
      if (state.match.matchId !== payload.matchId) {
        logger.warn('Ignoring mismatched match:answer_ack event', {
          activeMatchId: state.match.matchId,
          payloadMatchId: payload.matchId,
        });
        return state;
      }
      // (Removed the per-answer `answer_submitted` PostHog event — fully
      // redundant with the `match_answers` Postgres table.)
      const currentQIndex = state.match.currentQuestion?.qIndex;
      if (isStaleAnswerAck(payload, currentQIndex)) {
        logger.warn('Ignoring stale match:answer_ack event', {
          received: payload.qIndex,
          current: currentQIndex,
        });
        return state;
      }
      const existing = state.match.questions[payload.qIndex];
      const fallbackQuestion = existing?.payload ?? state.match.currentQuestion;
      if (!fallbackQuestion) return state;
      // When both players answer at the same moment this ack can beat
      // `match:opponent_answered`. Newer backends therefore attach the
      // opponent's outcome to the ack; apply it exactly like
      // setOpponentAnswered would. Older backends omit the fields, in which
      // case the opponent's points stay unknown until the event arrives.
      const carriesOpponent = payload.oppAnswered;
      return {
        match: {
          ...state.match,
          answerAck: payload,
          countdownGuessAck: null,
          opponentCountdownFoundCount: 0,
          cluesGuessAck: null,
          myTotalPoints: payload.myTotalPoints,
          // A delayed ack (computed before the opponent answered) must not
          // regress a newer `match:opponent_answered` for the same question;
          // stale acks are already rejected above, and the flag is reset per
          // question, so OR-ing is safe.
          opponentAnswered: state.match.opponentAnswered || payload.oppAnswered,
          opponentSelectedIndex:
            carriesOpponent && payload.opponentSelectedIndex !== undefined
              ? payload.opponentSelectedIndex
              : state.match.opponentSelectedIndex,
          oppTotalPoints:
            carriesOpponent && payload.opponentTotalPoints !== undefined
              ? payload.opponentTotalPoints
              : state.match.oppTotalPoints,
          opponentRecentPoints:
            carriesOpponent && payload.opponentPointsEarned !== undefined
              ? payload.opponentPointsEarned
              : state.match.opponentRecentPoints,
          opponentAnsweredCorrectly:
            carriesOpponent && payload.opponentIsCorrect !== undefined
              ? payload.opponentIsCorrect
              : state.match.opponentAnsweredCorrectly,
          questions: {
            ...state.match.questions,
            [payload.qIndex]: {
              payload: fallbackQuestion,
              correctIndex: state.match.questions[payload.qIndex]?.correctIndex,
            },
          },
        },
      };
    });
  },

  setCountdownGuessAck: (payload) => {
    logger.info('Realtime store set countdown guess ack', {
      matchId: payload.matchId,
      qIndex: payload.qIndex,
      accepted: payload.accepted,
      foundCount: payload.foundCount,
    });
    set((state) => {
      if (!state.match) return state;
      if (state.match.matchId !== payload.matchId) {
        logger.warn('Ignoring mismatched match:countdown_guess_ack event', {
          activeMatchId: state.match.matchId,
          payloadMatchId: payload.matchId,
        });
        return state;
      }
      const currentQIndex = state.match.currentQuestion?.qIndex;
      if (currentQIndex !== undefined && payload.qIndex !== currentQIndex) {
        logger.warn('Ignoring stale match:countdown_guess_ack event', {
          received: payload.qIndex,
          current: currentQIndex,
        });
        return state;
      }
      return {
        match: {
          ...state.match,
          countdownGuessAck: payload,
        },
      };
    });
  },

  setOpponentCountdownProgress: (payload) => {
    logger.info('Realtime store set opponent countdown progress', {
      matchId: payload.matchId,
      qIndex: payload.qIndex,
      foundCount: payload.foundCount,
    });
    set((state) => {
      if (!state.match) return state;
      if (state.match.matchId !== payload.matchId) return state;
      const currentQIndex = state.match.currentQuestion?.qIndex;
      if (currentQIndex !== undefined && payload.qIndex !== currentQIndex) return state;
      // Monotonic: only advance, never go backwards.
      if (payload.foundCount <= state.match.opponentCountdownFoundCount) return state;
      return {
        match: {
          ...state.match,
          opponentCountdownFoundCount: payload.foundCount,
        },
      };
    });
  },

  setCluesGuessAck: (payload) => {
    logger.info('Realtime store set clues guess ack', {
      matchId: payload.matchId,
      qIndex: payload.qIndex,
      clueIndex: payload.clueIndex,
      revealCount: payload.revealCount,
    });
    set((state) => {
      if (!state.match) return state;
      if (state.match.matchId !== payload.matchId) {
        logger.warn('Ignoring mismatched match:clues_guess_ack event', {
          activeMatchId: state.match.matchId,
          payloadMatchId: payload.matchId,
        });
        return state;
      }
      const currentQIndex = state.match.currentQuestion?.qIndex;
      if (currentQIndex !== undefined && payload.qIndex !== currentQIndex) {
        logger.warn('Ignoring stale match:clues_guess_ack event', {
          received: payload.qIndex,
          current: currentQIndex,
        });
        return state;
      }
      return {
        match: {
          ...state.match,
          cluesGuessAck: payload,
        },
      };
    });
  },

  setOpponentAnswered: (payload) => {
    logger.info('Realtime store set opponent answered', payload);
    set((state) => {
      if (!state.match) return state;
      if (payload?.matchId && state.match.matchId !== payload.matchId) {
        logger.warn('Ignoring mismatched match:opponent_answered event', {
          activeMatchId: state.match.matchId,
          payloadMatchId: payload.matchId,
        });
        return state;
      }
      const currentQIndex = state.match.currentQuestion?.qIndex;
      if (payload?.qIndex !== undefined && currentQIndex !== undefined && payload.qIndex !== currentQIndex) {
        logger.warn('Ignoring stale match:opponent_answered event', {
          received: payload.qIndex,
          current: currentQIndex,
        });
        return state;
      }
      return {
        match: {
          ...state.match,
          opponentAnswered: true,
          opponentSelectedIndex: payload?.selectedIndex ?? state.match.opponentSelectedIndex,
          oppTotalPoints: payload?.opponentTotalPoints ?? state.match.oppTotalPoints,
          opponentRecentPoints:
            payload?.pointsEarned !== undefined ? payload.pointsEarned : state.match.opponentRecentPoints,
          opponentAnsweredCorrectly:
            payload?.isCorrect !== undefined ? payload.isCorrect : state.match.opponentAnsweredCorrectly,
        },
      };
    });
  },

  setQuestionPhase: (phase) => {
    logger.info('Realtime store set question phase', { phase });
    set((state) => {
      if (!state.match) return state;
      return {
        match: {
          ...state.match,
          currentQuestionPhase: phase,
        },
      };
    });
  },

  setRoundResult: (payload) => {
    logger.info('Realtime store set round result', { matchId: payload.matchId, qIndex: payload.qIndex });
    set((state) => {
      if (!state.match) return state;
      if (state.match.matchId !== payload.matchId) {
        logger.warn('Ignoring mismatched match:round_result event', {
          activeMatchId: state.match.matchId,
          payloadMatchId: payload.matchId,
        });
        return state;
      }
      const currentQIndex = state.match.currentQuestion?.qIndex;
      if (currentQIndex !== undefined && payload.qIndex !== currentQIndex) {
        logger.warn('Ignoring stale match:round_result event', {
          received: payload.qIndex,
          current: currentQIndex,
        });
        return state;
      }
      const existing = state.match.questions[payload.qIndex];
      const fallbackQuestion = existing?.payload ?? state.match.currentQuestion;
      if (!fallbackQuestion) return state;
      const { myTotals, opponentTotals } = extractPlayerTotals(payload.players, state.selfUserId);
      return {
        match: {
          ...state.match,
          lastRoundResult: payload,
          countdownGuessAck: null,
          opponentCountdownFoundCount: 0,
          cluesGuessAck: null,
          myTotalPoints: myTotals?.totalPoints ?? state.match.myTotalPoints,
          oppTotalPoints: opponentTotals?.totalPoints ?? state.match.oppTotalPoints,
          // AI penalty answers deliberately skip the live
          // `match:opponent_answered` event so the keeper/shooter result is not
          // leaked before the round resolves. Reconcile the transient opponent
          // fields from the authoritative round result; otherwise the UI keeps
          // the question-reset defaults (notably `opponentRecentPoints = 0`)
          // while animating a real bot goal.
          opponentAnswered: opponentTotals ? true : state.match.opponentAnswered,
          opponentRecentPoints:
            opponentTotals?.pointsEarned ?? state.match.opponentRecentPoints,
          opponentAnsweredCorrectly:
            opponentTotals?.isCorrect ?? state.match.opponentAnsweredCorrectly,
          opponentSelectedIndex:
            opponentTotals?.selectedIndex ?? state.match.opponentSelectedIndex,
          questions: {
            ...state.match.questions,
            [payload.qIndex]: {
              payload: fallbackQuestion,
              correctIndex:
                payload.reveal.kind === 'multipleChoice'
                  ? payload.reveal.correctIndex
                  : state.match.questions[payload.qIndex]?.correctIndex,
              selfIsCorrect: myTotals?.isCorrect ?? state.match.questions[payload.qIndex]?.selfIsCorrect,
              opponentIsCorrect:
                opponentTotals?.isCorrect ?? state.match.questions[payload.qIndex]?.opponentIsCorrect,
            },
          },
        },
      };
    });
  },

  setFinalResults: (payload) => {
    logger.info('Realtime store set final results', {
      matchId: payload.matchId,
      winnerId: payload.winnerId,
      variant: payload.variant,
    });
    set((state) => {
      // match_completed analytics are emitted server-side (one event per
      // player, with a reliable opponent_is_ai flag) so the event fires even
      // when a player disconnects before final_results arrives. See
      // backend possession-completion.ts / party-quiz-match-flow.ts.
      if (!state.match) {
        const rejoin = state.rejoinMatch?.matchId === payload.matchId ? state.rejoinMatch : null;
        const sessionBelongsToPayload = state.sessionState?.activeMatchId === payload.matchId;
        const hasNewRealtimeContext = Boolean(state.lobby || state.draft);
        const hasDifferentSessionActivity = Boolean(
          state.sessionState &&
          !sessionBelongsToPayload &&
          state.sessionState.state !== 'IDLE',
        );
        if (hasNewRealtimeContext || hasDifferentSessionActivity) {
          logger.warn('Ignoring stale match:final_results event without active match', {
            payloadMatchId: payload.matchId,
            lobbyId: state.lobby?.lobbyId ?? null,
            draftLobbyId: state.draft?.lobbyId ?? null,
            sessionState: state.sessionState?.state ?? null,
            sessionActiveMatchId: state.sessionState?.activeMatchId ?? null,
          });
          return state;
        }
        return {
          matchPaused: false,
          pauseUntil: null,
          pausedAt: null,
          remainingReconnects: null,
          draftPaused: false,
          draftPauseUntil: null,
          draftDisconnectedUserId: null,
          rejoinMatch: null,
          forfeitPending: null,
          partyDropout: null,
          match: constructFallbackMatchFromResults(payload, state.selfUserId, rejoin),
        };
      }
      if (state.match.matchId !== payload.matchId) {
        logger.warn('Ignoring mismatched match:final_results event', {
          activeMatchId: state.match.matchId,
          payloadMatchId: payload.matchId,
        });
        return state;
      }
      const participants = payload.participants ?? state.match.participants;
      const opponent = mergeOpponentFromFinalParticipants(
        payload.participants,
        participants,
        state.selfUserId,
        state.match.opponent,
      );
      return {
        matchPaused: false,
        pauseUntil: null,
        pausedAt: null,
        remainingReconnects: null,
        draftPaused: false,
        draftPauseUntil: null,
        draftDisconnectedUserId: null,
        rejoinMatch: null,
        forfeitPending: null,
        partyDropout: null,
        match: {
          ...state.match,
          variant: payload.variant ?? (payload.standings ? 'friendly_party_quiz' : state.match.variant),
          opponent,
          participants,
          countdownEndsAt: null,
          countdownReason: null,
          waitingForReady: null,
          finalResults: payload,
        },
      };
    });
  },

  setMatchCancelled: (payload) => {
    logger.info('Realtime store set match cancelled', payload);
    set({
      match: null,
      cancelledMatch: payload,
      matchPaused: false,
      pauseUntil: null,
      pausedAt: null,
      remainingReconnects: null,
      draftPaused: false,
      draftPauseUntil: null,
      draftDisconnectedUserId: null,
      rejoinMatch: null,
      forfeitPending: null,
      partyDropout: null,
      error: null,
    });
  },

  triggerDevPossessionAnimation: ({ result, attackerSeat }) => {
    const id = Date.now();
    logger.info('Realtime store trigger dev possession animation', { id, result, attackerSeat });
    set({ devPossessionAnimation: { id, result, attackerSeat } });
  },

  clearDevPossessionAnimation: () => {
    set({ devPossessionAnimation: null });
  },

  exitCompletedMatchToLobby: () => {
    logger.info('Realtime store exit completed match to lobby');
    set({
      draft: null,
      match: null,
      matchPaused: false,
      pauseUntil: null,
      pausedAt: null,
      remainingReconnects: null,
      draftPaused: false,
      draftPauseUntil: null,
      draftDisconnectedUserId: null,
      rejoinMatch: null,
      partyDropout: null,
      devPossessionAnimation: null,
      error: null,
    });
  },
});
